import { cn } from '@/lib/utils';

export type EventStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'archived';

interface EventStatusBadgeProps {
  status: EventStatus;
  className?: string;
  /** Use 'solid' variant for dark backgrounds (e.g. the hero banner). */
  variant?: 'default' | 'solid';
}

interface StatusVariantStyles {
  label: string;
  default: string;
  solid: string;
  dotDefault: string;
  dotSolid: string;
}

const statusConfig: Record<EventStatus, StatusVariantStyles> = {
  draft: {
    label: 'Draft',
    default: 'bg-muted-foreground/15 text-muted-foreground',
    solid: 'bg-muted-foreground text-white',
    dotDefault: 'bg-muted-foreground/70',
    dotSolid: 'bg-white',
  },
  scheduled: {
    label: 'Scheduled',
    default: 'bg-blue-400/15 text-blue-600',
    solid: 'bg-blue-500 text-white',
    dotDefault: 'bg-blue-400',
    dotSolid: 'bg-white',
  },
  active: {
    label: 'Active',
    default: 'bg-emerald-400/15 text-emerald-600',
    solid: 'bg-emerald-500 text-white',
    dotDefault: 'bg-emerald-400',
    dotSolid: 'bg-white',
  },
  paused: {
    label: 'Paused',
    default: 'bg-amber-400/15 text-amber-600',
    solid: 'bg-amber-500 text-white',
    dotDefault: 'bg-amber-400',
    dotSolid: 'bg-white',
  },
  completed: {
    label: 'Completed',
    default: 'bg-purple-400/15 text-purple-600',
    solid: 'bg-purple-500 text-white',
    dotDefault: 'bg-purple-400',
    dotSolid: 'bg-white',
  },
  archived: {
    label: 'Archived',
    default: 'bg-muted-foreground/10 text-muted-foreground',
    solid: 'bg-muted-foreground/80 text-white',
    dotDefault: 'bg-muted-foreground/50',
    dotSolid: 'bg-white',
  },
};

export function EventStatusBadge({
  status,
  className,
  variant = 'default',
}: EventStatusBadgeProps) {
  const config = statusConfig[status];
  if (!config) return null;

  const containerClass = variant === 'solid' ? config.solid : config.default;
  const dotClass = variant === 'solid' ? config.dotSolid : config.dotDefault;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        containerClass,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', dotClass)} />
      {config.label}
    </span>
  );
}
